/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useLocation } from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';

import type { ConsumerPageHeader_EnterpriseProgramFragment as EnterpriseProgramFragment } from 'bundles/composable-page-header-consumer/queries/__generated__/ConsumerMyPageHeaderQuery';
import {
  ORG_SCOPED_ROUTE_PREFIX,
  isOnOrgScopedRoute,
} from 'bundles/composable-page-header-enterprise/utils/orgScopedRouteUtils';
import EnterpriseSearchBar from 'bundles/search-common/components/searchbar/enterprise/SearchBar';

import _t from 'i18n!nls/composable-page-header-enterprise';

type Props = {
  thirdPartyOrganizationSlug?: string;
  enrolledEnterprisePrograms?: Partial<EnterpriseProgramFragment>[] | null;
  chosenProgramSlug?: string;
};

const styles = {
  searchBar: css`
    width: 100%;
    max-width: 480px;
    min-width: 0;

    ${breakpoints.up('lg')} {
      width: 100%;
    }
  `,
};

export function EnterpriseOrgScopedSearchBarGridItem(props: Props) {
  const { thirdPartyOrganizationSlug, enrolledEnterprisePrograms, chosenProgramSlug } = props;
  const { pathname } = useLocation();
  const onOrgScopedRoute = isOnOrgScopedRoute(pathname);

  if (!thirdPartyOrganizationSlug) {
    return null;
  }

  // For non-org-scoped mode, enrollment data is required to determine program context.
  if (!onOrgScopedRoute && !enrolledEnterprisePrograms?.length) {
    return null;
  }

  const orgPrograms = enrolledEnterprisePrograms?.filter(
    (program) => program?.naptimeThirdPartyOrganization?.slug === thirdPartyOrganizationSlug
  );

  const programIds: Array<string> = orgPrograms?.map((program) => program?.id ?? '') ?? [];
  const primaryProgramSlug = chosenProgramSlug ?? orgPrograms?.[0]?.slug;

  // For non-org-scoped mode, a program slug is needed for the browse path.
  if (!onOrgScopedRoute && !primaryProgramSlug) {
    return null;
  }

  const orgName = orgPrograms?.[0]?.naptimeThirdPartyOrganization?.name;
  const searchPath = onOrgScopedRoute
    ? `${ORG_SCOPED_ROUTE_PREFIX}${thirdPartyOrganizationSlug}/search`
    : `/programs/${primaryProgramSlug}/browse`;

  return (
    <div data-testid="org-scoped-enterprise-search-bar" css={styles.searchBar}>
      <EnterpriseSearchBar
        enterpriseSearchBarConfig={{
          programIds,
          pathname: searchPath,
          searchResultsBasePath: searchPath,
          placeholder: orgName
            ? _t('Search #{orgName} learning on Coursera', { orgName })
            : _t('Search learning on Coursera'),
          ...(onOrgScopedRoute
            ? { enterpriseOrgSlug: thirdPartyOrganizationSlug }
            : { enterpriseProgramSlug: primaryProgramSlug }),
        }}
      />
    </div>
  );
}

export default EnterpriseOrgScopedSearchBarGridItem;
